import type { ReactNode } from 'react';
import { ApiFailure } from '../api/errors';

/**
 * The handful of shared pieces. No component library, for `apps/admin`'s reason:
 * the screens are a list, a grid of buttons and one form, and a kit would bring
 * a design language to fight with three presets plus a bundle to ship on every
 * tenant's phone.
 *
 * Two accessibility rules are kept here rather than remembered per screen:
 *
 *  - **Anything that acts is a `<button type="button">`.** A slot is a button, a
 *    day is a button, "Book" is a button. A `div` with an `onClick` is invisible
 *    to a keyboard and to a screen reader, and this is a page a stranger with
 *    any device may land on from a search result.
 *  - **Anything that changes without a click announces itself.** The OTP step,
 *    the availability answer and every failure live in a `role="status"` or
 *    `role="alert"` region, so a guest who cannot see the layout still hears
 *    "we sent a code" and "that time was taken".
 */

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'error';
  title?: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div
      className={`notice notice--${tone}`}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
    >
      {title === undefined ? null : <strong className="notice__title">{title}</strong>}
      {children === undefined ? null : <div className="notice__body">{children}</div>}
    </div>
  );
}

/**
 * A failure, in the server's own words.
 *
 * `guest-api` writes sentences a guest can act on — "We cannot send a code to
 * that number", "That time was taken while we were booking it" — and replacing
 * them with a generic apology would throw away the useful half of §6.4.4's
 * deliberate exception to the identical-answer rule. So the message is printed
 * as it came, and only the *framing* is ours.
 */
export function Failure({ error }: { error: unknown }): ReactNode {
  const failure = error instanceof ApiFailure ? error : null;
  if (failure === null) {
    return <Notice tone="error" title="Something went wrong" />;
  }

  const retry =
    failure.retryAfterSeconds === undefined
      ? null
      : ` Try again in ${String(failure.retryAfterSeconds)} seconds.`;

  return (
    <Notice tone={failure.code === 'offline' ? 'warn' : 'error'} title={failure.message}>
      {retry === null ? null : <p className="notice__hint">{retry}</p>}
    </Notice>
  );
}

export function Busy({ label }: { label: string }): ReactNode {
  return (
    <p className="busy" role="status" aria-live="polite">
      {label}
    </p>
  );
}

/** A labelled input. The label is a real `<label for>`, never a placeholder. */
export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint === undefined ? null : (
        <p className="field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
    </div>
  );
}
